import * as zExt from "@redotech/rpc/ext";
import * as z from "zod";

export const GorgiasVisibilityStatus = {
  PUBLIC: "PUBLIC",
  UNLISTED: "UNLISTED",
} as const;
export type GorgiasVisibilityStatus =
  (typeof GorgiasVisibilityStatus)[keyof typeof GorgiasVisibilityStatus];

const gorgiasVisibilityStatusSchema = z.enum(["PUBLIC", "UNLISTED"]);

const GorgiasSeoMeta_ = z.object({
  title: z.string().nullable(),
  description: z.string().nullable(),
});
export const GorgiasSeoMeta: z.ZodType<GorgiasSeoMeta> = GorgiasSeoMeta_;
export interface GorgiasSeoMeta extends z.infer<typeof GorgiasSeoMeta_> {}

const GorgiasArticleTranslation_ = z.object({
  locale: z.string(),
  title: z.string(),
  excerpt: z.string().optional(),
  content: z.string(),
  slug: z.string(),
  seo_meta: GorgiasSeoMeta,
  visibility_status: gorgiasVisibilityStatusSchema,
});
export const GorgiasArticleTranslation: z.ZodType<GorgiasArticleTranslation> =
  GorgiasArticleTranslation_;
export interface GorgiasArticleTranslation
  extends z.infer<typeof GorgiasArticleTranslation_> {}

const GorgiasArticle_ = z.object({
  id: z.number(),
  category_id: z.number(),
  available_locales: z.array(z.string()),
  translation_versions: z.object({
    current: GorgiasArticleTranslation,
    draft: GorgiasArticleTranslation.optional(),
  }),
});
export const GorgiasArticle: z.ZodType<GorgiasArticle> = GorgiasArticle_;
export interface GorgiasArticle extends z.infer<typeof GorgiasArticle_> {}

const GorgiasCategoryTranslation_ = z.object({
  title: z.string(),
  description: z.string().optional(),
  slug: z.string().optional(),
  seo_meta: GorgiasSeoMeta.optional(),
  visibility_status: gorgiasVisibilityStatusSchema,
  image_url: z.string().nullable().optional(),
});
export const GorgiasCategoryTranslation: z.ZodType<GorgiasCategoryTranslation> =
  GorgiasCategoryTranslation_;
export interface GorgiasCategoryTranslation
  extends z.infer<typeof GorgiasCategoryTranslation_> {}

const GorgiasCategory_ = z.object({
  id: z.number(),
  help_center_id: z.number(),
  available_locales: z.array(z.string()),
  children: z.array(z.lazy(() => GorgiasCategory)).optional(),
  translation: GorgiasCategoryTranslation,
  articles: z.array(GorgiasArticle),
});
export const GorgiasCategory: z.ZodType<GorgiasCategory> = GorgiasCategory_;
export interface GorgiasCategory extends z.infer<typeof GorgiasCategory_> {}

const GorgiasFaqsResponse_ = z.object({
  id: z.number(),
  help_center_id: z.number(),
  children: z.array(GorgiasCategory),
  translation: GorgiasCategoryTranslation.nullable().optional(),
});
export const GorgiasFaqsResponse: z.ZodType<GorgiasFaqsResponse> =
  GorgiasFaqsResponse_;
export interface GorgiasFaqsResponse
  extends z.infer<typeof GorgiasFaqsResponse_> {}

export const importGorgiasFaqs = {
  name: "importGorgiasFaqs" as const,
  input: z.object({
    teamId: zExt.objectId(),
    gorgiasFaqsJson: GorgiasFaqsResponse,
    createFaqPage: z.boolean().optional().default(true),
    faqPageTitle: z.string().optional().default("Help Center"),
    publishImportedContent: z.boolean().optional().default(false),
  }),
  output: z.object({
    success: z.boolean(),
    collectionsCreated: z.number(),
    articlesCreated: z.number(),
    collectionsSkipped: z.number(),
    articlesSkipped: z.number(),
    faqPageId: z.string().optional(),
    errors: z.array(z.string()).optional(),
  }),
};
